package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.InventoryItem
import fr.manyverse.app.domain.model.InventoryItemToCreate
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.usecase.CreateInventoryItemUseCase
import fr.manyverse.app.domain.usecase.DeleteInventoryItemImageUseCase
import fr.manyverse.app.domain.usecase.DeleteInventoryItemUseCase
import fr.manyverse.app.domain.usecase.GetInventoryItemsUseCase
import fr.manyverse.app.domain.usecase.NotifyItemSentUseCase
import fr.manyverse.app.domain.usecase.ObserveInventoryItemsUseCase
import fr.manyverse.app.domain.usecase.TransferInventoryItemUseCase
import fr.manyverse.app.domain.usecase.UpdateInventoryItemQuantityUseCase
import fr.manyverse.app.domain.usecase.UploadInventoryItemImageUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.error.toUserMessage
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch

class InventoryViewModel(
    private val characterId: String,
    private val tableId: String,
    private val currentUserId: String,
    private val observeInventoryItemsUseCase: ObserveInventoryItemsUseCase,
    private val getInventoryItemsUseCase: GetInventoryItemsUseCase,
    private val createInventoryItemUseCase: CreateInventoryItemUseCase,
    private val deleteInventoryItemUseCase: DeleteInventoryItemUseCase,
    private val updateInventoryItemQuantityUseCase: UpdateInventoryItemQuantityUseCase,
    private val uploadInventoryItemImageUseCase: UploadInventoryItemImageUseCase,
    private val deleteInventoryItemImageUseCase: DeleteInventoryItemImageUseCase,
    private val transferInventoryItemUseCase: TransferInventoryItemUseCase,
    private val notifyItemSentUseCase: NotifyItemSentUseCase,
    private val imageStorageRepository: ImageStorageRepository,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _itemsState = MutableStateFlow<AsyncUiState<List<InventoryItem>>>(AsyncUiState.Idle)
    val itemsState: StateFlow<AsyncUiState<List<InventoryItem>>> = _itemsState.asStateFlow()

    private val _createItemState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val createItemState: StateFlow<AsyncUiState<Unit>> = _createItemState.asStateFlow()

    private val _deleteItemState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val deleteItemState: StateFlow<AsyncUiState<Unit>> = _deleteItemState.asStateFlow()

    private val _giveItemState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val giveItemState: StateFlow<AsyncUiState<Unit>> = _giveItemState.asStateFlow()

    // Optimistic quantity overrides: itemId -> pending quantity
    private val _pendingQuantities = MutableStateFlow<Map<String, Int>>(emptyMap())
    val pendingQuantities: StateFlow<Map<String, Int>> = _pendingQuantities.asStateFlow()

    private val quantityJobs = mutableMapOf<String, Job>()

    private var observeJob: Job? = null

    init {
        observeItems()
    }

    /**
     * Observe l'inventaire en temps réel. Relancée par le bouton « Réessayer » : si le chargement
     * initial échoue, le flux se termine et l'écran affiche l'erreur.
     */
    fun observeItems() {
        observeJob?.cancel()
        observeJob = viewModelScope.launch {
            _itemsState.value = AsyncUiState.Loading
            observeInventoryItemsUseCase(characterId)
                .catch { _itemsState.value = AsyncUiState.Error(it.toUserMessage()) }
                .collect { items -> _itemsState.value = AsyncUiState.Success(items) }
        }
    }

    fun loadItems() {
        launchAsync(_itemsState) {
            getInventoryItemsUseCase(characterId)
        }
    }

    fun createItem(
        name: String,
        category: ItemCategory,
        quantity: Int,
        imageData: ImageData? = null
    ) {
        launchAsyncWithCallback(
            state = _createItemState,
            block = {
                val itemToCreate = InventoryItemToCreate(
                    characterId = characterId,
                    name = name,
                    category = category,
                    quantity = quantity
                )

                val createResult = createInventoryItemUseCase(itemToCreate)
                if (createResult.isFailure) return@launchAsyncWithCallback createResult.map { }

                val createdItem = createResult.getOrThrow()

                if (imageData != null) {
                    val uploadResult = uploadInventoryItemImageUseCase(characterId, createdItem.id, imageData)
                    if (uploadResult.isFailure) return@launchAsyncWithCallback uploadResult.map { }
                }

                Result.success(Unit)
            },
            onSuccess = { loadItems() }
        )
    }

    fun deleteItem(item: InventoryItem) {
        launchAsyncWithCallback(
            state = _deleteItemState,
            block = { deleteInventoryItemUseCase(item).map { } },
            onSuccess = { loadItems() }
        )
    }

    fun incrementQuantity(item: InventoryItem) {
        val current = _pendingQuantities.value[item.id] ?: item.quantity
        val newQty = (current + 1).coerceAtMost(999)
        updateQuantityOptimistic(item.id, newQty)
    }

    fun decrementQuantity(item: InventoryItem) {
        val current = _pendingQuantities.value[item.id] ?: item.quantity
        val newQty = (current - 1).coerceAtLeast(1)
        updateQuantityOptimistic(item.id, newQty)
    }

    private fun updateQuantityOptimistic(itemId: String, newQuantity: Int) {
        _pendingQuantities.value += (itemId to newQuantity)

        quantityJobs[itemId]?.cancel()
        quantityJobs[itemId] = viewModelScope.launch {
            delay(500)
            updateInventoryItemQuantityUseCase(itemId, newQuantity)
                .onSuccess { updatedItem ->
                    _pendingQuantities.value -= itemId
                    val current = (_itemsState.value as? AsyncUiState.Success)?.data ?: return@onSuccess
                    _itemsState.value = AsyncUiState.Success(current.map { if (it.id == updatedItem.id) updatedItem else it })
                }
                .onFailure { error ->
                    // La quantité affichée revient à celle enregistrée
                    _pendingQuantities.value -= itemId
                    errorNotifier.notify(error)
                }
            quantityJobs.remove(itemId)
        }
    }

    suspend fun getSignedImageUrl(imagePath: String): Result<String> {
        return imageStorageRepository.getSignedUrl(imagePath)
    }

    /**
     * Donne l'objet à un autre personnage. Le destinataire est notifié et le MJ voit l'échange
     * dans l'historique : l'objet est déjà transféré, un échec de cette notification est ignoré.
     */
    fun giveItem(
        item: InventoryItem,
        toCharacterId: String,
        quantity: Int,
        senderName: String,
        recipientUserId: String?,
        recipientName: String
    ) {
        launchAsyncWithCallback(
            state = _giveItemState,
            block = {
                transferInventoryItemUseCase(item, toCharacterId, quantity).onSuccess {
                    if (recipientUserId != null) {
                        notifyItemSentUseCase(
                            tableId = tableId,
                            triggeredBy = currentUserId,
                            recipientUserIds = listOf(recipientUserId),
                            senderName = senderName,
                            recipientNames = listOf(recipientName),
                            itemName = item.name,
                            quantity = quantity
                        )
                    }
                }
            },
            onSuccess = { loadItems() }
        )
    }

    fun resetCreateItemState() {
        resetState(_createItemState)
    }

    fun resetGiveItemState() {
        resetState(_giveItemState)
    }
}
